import * as React from 'react';
import { type VariantProps, cva } from 'class-variance-authority';

import { cn } from '@components/lib/utils';

/* ─────────────────────────────────────────────────────────────────────────
 *  CountBadge — ONE shared, round number-bubble (#558 consolidation epic, #2).
 *
 *  Replaces 6+ near-identical inline re-implementations of the little
 *  `rounded-full` bubble that shows a count next to a tab/label/trigger
 *  (recommendation count, task count, active-filter count, attention count,
 *  ranked-list index, …). Every original was the same handful of utility
 *  classes copy-pasted with a different colour/size; this collapses them to a
 *  single tokened component so the bubble looks identical everywhere and can be
 *  restyled in one place.
 *
 *  Contract: it renders exactly the number it is GIVEN (including 0). It does
 *  NOT decide whether to show itself — callers keep their own
 *  `{count > 0 && <CountBadge … />}` guards, so migrating a site is a pure
 *  swap with zero behavioural change. Layout-only tweaks each site needs
 *  (`ml-1`, `shrink-0`, `tabular-nums`, absolute positioning) flow through
 *  `className`.
 *
 *  `variant` covers the real colours in use (blue / gray / red); `size`
 *  covers the real `size-4 / size-5 / size-6` fixed circles plus a `pill`
 *  that grows horizontally (the notification badge + the `max` overflow case).
 * ───────────────────────────────────────────────────────────────────────── */

const countBadgeVariants = cva(
  'inline-flex items-center justify-center rounded-full font-bold',
  {
    variants: {
      variant: {
        // Blue solid — the default tab/filter count bubble.
        primary: 'bg-marble-blue-500 text-white',
        // Gray subtle — neutral counts / ranked-list indices.
        neutral: 'bg-marble-bg-100 text-marble-bg-700',
        // Red solid — attention / notification counts.
        danger: 'bg-marble-red-500 text-white',
      },
      size: {
        sm: 'size-4 text-[10px]',
        md: 'size-5 text-xs',
        lg: 'size-6 text-xs',
        // Grows with content (multi-digit / "99+") instead of a fixed circle.
        pill: 'min-w-3.5 px-1 text-[9px]',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'sm',
    },
  },
);

export type CountBadgeVariant = NonNullable<
  VariantProps<typeof countBadgeVariants>['variant']
>;
export type CountBadgeSize = NonNullable<
  VariantProps<typeof countBadgeVariants>['size']
>;

export interface CountBadgeProps
  extends Omit<React.ComponentProps<'span'>, 'children'>,
    VariantProps<typeof countBadgeVariants> {
  /** The number to show. Rendered verbatim, including 0. */
  count: number;
  /** When `count > max`, render `${max}+` (e.g. 99 → "99+"). */
  max?: number;
}

function CountBadge({
  count,
  variant,
  size,
  max,
  className,
  ...props
}: CountBadgeProps) {
  const label = max != null && count > max ? `${max}+` : `${count}`;

  return (
    <span
      data-slot="count-badge"
      className={cn(countBadgeVariants({ variant, size }), className)}
      {...props}
    >
      {label}
    </span>
  );
}

export { CountBadge, countBadgeVariants };
